import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

// Business options - replace with API call
const businessOptions = [
  { id: 1, name: 'a1 Technologies' },
  { id: 2, name: 'a1 Retail Solutions' },
  { id: 3, name: 'a1 Consulting' },
];

const AddDepartment = ({ onDepartmentAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    businessId: '',
    departmentName: '',
    description: '',
    status: 'active',
  });

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      businessId: '',
      departmentName: '',
      description: '',
      status: 'active',
    });
    setErrors({});
    setSuccessMessage('');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.businessId) newErrors.businessId = 'Business is required';
    if (!formData.departmentName) newErrors.departmentName = 'Department name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle add department
  const handleAddDepartment = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Simulate API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newDepartment = {
        ...formData,
        id: Date.now(), // Generate unique ID
        createdDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
      };

      // Call parent callback to update department list
      if (onDepartmentAdded) {
        onDepartmentAdded(newDepartment);
      }

      setSuccessMessage('Department added successfully!');
      setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to add department. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dialog open/close
  const handleOpen = () => {
    resetForm();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{ textTransform: 'none' }}
      >
        Add Department
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          Add New Department
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Business/Company Name */}
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.businessId)}>
                <InputLabel>Business / Company Name *</InputLabel>
                <Select
                  value={formData.businessId}
                  onChange={(e) => handleInputChange('businessId', e.target.value)}
                  label="Business / Company Name *"
                >
                  {businessOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.businessId && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.businessId}
                  </Alert>
                )}
              </FormControl>
            </Grid>

            {/* Department Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Department Name *"
                value={formData.departmentName}
                onChange={(e) => handleInputChange('departmentName', e.target.value)}
                error={Boolean(errors.departmentName)}
                helperText={errors.departmentName}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddDepartment}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Adding...' : 'Add Department'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddDepartment;
